import {ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit, ViewChild} from '@angular/core';
import {calculateMaxDateFromValidatorsFor, calculateMinDateFromValidatorsFor} from '@aviary/forms/fields/date-form-field/date.form-validators';
import {
  DateRange,
  ExtractDateTypeFromSelection,
  MatDatepickerInputEvent,
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate
} from '@angular/material/datepicker';
import {toDateOnlyRangeIsoString, toDateRangeObject, toLocalMidnight} from '@aviary/store/common/date.helpers';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {BaseFormFieldComponent} from '@aviary/forms/fields/base-form-field/base-form-field.component';
import {NgClass} from '@angular/common';
import {MatInput, MatSuffix} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {ReactiveFormsModule} from '@angular/forms';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';

@Component({
  selector: 'mx-date-range-form-field',
  templateUrl: './date-range-form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(DateRangeFormFieldComponent)],
  imports: [
    BaseFormFieldComponent, NgClass, MatInput, MatDateRangeInput, MatDatepickerToggle, MatIcon, MatDateRangePicker, MatStartDate, MatEndDate,
    MatSuffix, ReactiveFormsModule
  ]
})
export class DateRangeFormFieldComponent implements OnInit, ControlRef {

  @ViewChild('picker') public picker: MatDateRangePicker<Date>;

  @Input() public fieldName: string;
  @Input() public overrideLabel?: string;
  @Input() public externalLabel = false;
  @Input() public formGroup: ExtendedFormGroup;

  // See commentary for these fields in DateFormFieldComponent.  Both this component and that one calculate these values based on the same validators.
  public minDate?: Date;
  public maxDate?: Date;

  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.
  public disabled: boolean; // For disabling elements that the form control is unaware of.
  public startDate?: Date;
  public endDate?: Date;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.control = getExtendedControlFrom(this.formGroup, this.fieldName);
    this.minDate = calculateMinDateFromValidatorsFor(this.control);
    this.maxDate = calculateMaxDateFromValidatorsFor(this.control);

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        ({start: this.startDate, end: this.endDate} = toDateRangeObject(value));
      });

    this.control.registerOnDisabledChange(disabled => this.disabled = disabled);
  }

  public onClick(): void {
    if (!this.disabled) this.picker.open();  // No need to worry about closing... the picker is modal, so clicking anywhere outside of it will close it.
  }

  public onOpenedPicker(): void {
    /*
      Technically, when the user opens the date picker (and there wasn't a date range value already), focus has left this control's element and there's
      no value yet.  So this field naturally displays an error if it has a required validator.

      But for this particular component, it's too early to show the error in that case (the user wasn't given a chance to pick a range yet), so we mark this
      control as untouched, which clears the error state.
    */
    if (!this.control.value) this.control.markAsUntouched();
  }

  public onClosedPicker(): void {
    /*
      When the user dismisses the picker, we manually mark the control as touched so we can show an error state if they dismissed the picker without
      picking a date range.  This undoes the meddling we needed to do in onOpenedPicker().
    */
    this.control.markAsTouched();

    if (this.startDate && !this.endDate) {
      this.endDate = this.startDate;
      this.updateFormFieldValue();
    }
  }

  public onDateChangeForStart(event: MatDatepickerInputEvent<ExtractDateTypeFromSelection<DateRange<Date | undefined>>, DateRange<Date | undefined>>): void {
    this.startDate = event.value ? toLocalMidnight(event.value) : undefined;

    this.updateFormFieldValue();
  }

  public onDateChangeForEnd(event: MatDatepickerInputEvent<ExtractDateTypeFromSelection<DateRange<Date | undefined>>, DateRange<Date | undefined>>): void {
    this.endDate = event.value ? toLocalMidnight(event.value) : undefined;

    this.updateFormFieldValue();
  }

  private updateFormFieldValue(): void {
    this.control.patchValueAndMarkAsDirty(toDateOnlyRangeIsoString(this.startDate, this.endDate), {emitEvent: false});
  }
}
